import { useNavigate } from "react-router-dom";
import { InteractiveCard } from "../../../components/PromotionIQ/InteractiveCard";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import {
  learnings,
  historicalPromotions,
  formatCurrency,
  getRetailerName,
  getSkuInfo,
} from "../../../data/PromotionIQ/PromotionData";
import { Lightbulb, TrendingUp, TrendingDown, AlertCircle, ChevronRight, Target, Zap } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import React from "react";
import { usePromotionSelection } from "../../../components/PromotionIQ/PromotionSelectionContext";

interface LearningWithDetails {
  promotion_id: string;
  retailer_name: string;
  retailer_id: string;
  product: string;
  sku_id: string;
  planned_margin: number;
  actual_margin: number;
  variance: number;
  variance_pct: number;
  root_cause: string;
  learning: string;
  confidence_update: number;
}

const Learnings = () => {
  const navigate = useNavigate();
  const { setSelectedPromotion } = usePromotionSelection();

  // Enrich learnings with promotion details
  const enrichedLearnings: LearningWithDetails[] = learnings.map((l) => {
    const promo = historicalPromotions.find((p) => p.promotion_id === l.promotion_id);
    const variance = l.actual_margin - l.planned_margin;
    const variance_pct = ((variance) / Math.abs(l.planned_margin)) * 100;
    
    return {
      ...l,
      retailer_name: promo ? getRetailerName(promo.retailer_id) : "Unknown",
      retailer_id: promo?.retailer_id || "R1",
      product: promo ? getSkuInfo(promo.sku_id) : "Unknown",
      sku_id: promo?.sku_id || "S1",
      variance,
      variance_pct,
    };
  });

  const chartData = enrichedLearnings.map((l) => ({
    id: l.promotion_id,
    Planned: l.planned_margin,
    Actual: l.actual_margin,
  }));

  const totalPlanned = enrichedLearnings.reduce((sum, l) => sum + l.planned_margin, 0);
  const totalActual = enrichedLearnings.reduce((sum, l) => sum + l.actual_margin, 0);
  const avgConfidenceChange = enrichedLearnings.reduce((sum, l) => sum + l.confidence_update, 0) / enrichedLearnings.length;

  // Group by root cause
  const rootCauseCounts = enrichedLearnings.reduce((acc, l) => {
    acc[l.root_cause] = (acc[l.root_cause] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Learning & Assumption Update"
        question="What do we now know that we didn't before?"
      />

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <InteractiveCard
          label="Planned Total Margin"
          value={formatCurrency(totalPlanned)}
          subValue="Expected outcome"
          linkTo="/promotioniq/promotion-portfolio"
          linkLabel="View portfolio"
        />
        <InteractiveCard
          label="Actual Total Margin"
          value={formatCurrency(totalActual)}
          subValue="Realized outcome"
          variant={totalActual >= totalPlanned ? "success" : "warning"}
          linkTo="/promotioniq/monitoring"
          linkLabel="See monitoring"
        />
        <InteractiveCard
          label="Total Variance"
          value={formatCurrency(totalActual - totalPlanned)}
          subValue={`${(((totalActual - totalPlanned) / totalPlanned) * 100).toFixed(1)}% difference`}
          variant={totalActual >= totalPlanned ? "success" : "warning"}
        />
        <InteractiveCard
          label="Avg Confidence Adj."
          value={`${avgConfidenceChange >= 0 ? "+" : ""}${(avgConfidenceChange * 100).toFixed(0)}%`}
          subValue="Model calibration"
          variant={avgConfidenceChange >= 0 ? "success" : "warning"}
          linkTo="/promotioniq/baseline-diagnostics"
          linkLabel="View diagnostics"
        />
      </div>

      {/* Root Cause Summary */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {Object.entries(rootCauseCounts).map(([cause, count]) => (
          <div 
            key={cause}
            className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
          >
            <div className="flex items-center justify-between">
              <AlertCircle className={`h-5 w-5 ${
                cause === "Elasticity Miss" ? "text-warning" :
                cause === "Execution Failure" ? "text-destructive" :
                cause === "Timing Distortion" ? "text-primary" : "text-muted-foreground"
              }`} />
              <span className="text-2xl font-bold">{count}</span>
            </div>
            <p className="text-sm font-medium mt-2">{cause}</p>
            <p className="text-xs text-muted-foreground">occurrences</p>
          </div>
        ))}
      </div>

      {/* Planned vs Actual Chart */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">Planned vs Actual Margin</h3>
          <button 
            onClick={() => navigate("/promotioniq/retailer-promotion-truth")}
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            View Promotion Truth <ChevronRight className="h-3 w-3" />
          </button>
        </div>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 15%, 25%)" />
              <XAxis dataKey="id" stroke="hsl(220, 10%, 55%)" fontSize={12} />
              <YAxis stroke="hsl(220, 10%, 55%)" fontSize={12} tickFormatter={(v) => `$${v / 1000}K`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(220, 18%, 13%)",
                  border: "1px solid hsl(220, 15%, 25%)",
                  borderRadius: "0",
                }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <ReferenceLine y={0} stroke="hsl(220, 10%, 40%)" />
              <Bar dataKey="Planned" fill="hsl(220, 10%, 40%)" name="Planned" />
              <Bar dataKey="Actual" fill="hsl(32, 95%, 55%)" name="Actual" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Learnings Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {enrichedLearnings.map((learning) => (
          <div
            key={learning.promotion_id}
            className="border border-border bg-card p-6 hover:border-primary/50 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 
                  onClick={() => {
                    setSelectedPromotion(learning.promotion_id);
                    navigate("/promotioniq/promotion-builder");
                  }}
                  className="font-semibold text-primary cursor-pointer hover:underline"
                >
                  {learning.promotion_id}
                </h4>
                <p className="text-sm text-muted-foreground">
                  <span 
                    onClick={() => navigate(`/retailer-economics/${learning.retailer_id}`)}
                    className="hover:text-primary hover:underline cursor-pointer"
                  >
                    {learning.retailer_name}
                  </span>
                  {" • "}
                  {learning.product}
                </p>
              </div>
              <div className={`flex items-center gap-1 ${
                learning.variance >= 0 ? "text-success" : "text-destructive"
              }`}>
                {learning.variance >= 0 ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                <span className="font-medium">
                  {learning.variance >= 0 ? "+" : ""}{formatCurrency(learning.variance)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 bg-muted/30">
                <p className="text-xs text-muted-foreground">Planned</p>
                <p className="text-lg font-bold">{formatCurrency(learning.planned_margin)}</p>
              </div>
              <div className="text-center p-3 bg-muted/30">
                <p className="text-xs text-muted-foreground">Actual</p>
                <p className={`text-lg font-bold ${
                  learning.actual_margin >= 0 ? "text-success" : "text-destructive"
                }`}>
                  {formatCurrency(learning.actual_margin)}
                </p>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Root Cause</p>
                  <p className="text-sm">{learning.root_cause}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Learning</p>
                  <p className="text-sm">{learning.learning}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Confidence Adjustment</span>
              <span className={`font-medium ${
                learning.confidence_update >= 0 ? "text-success" : "text-destructive"
              }`}>
                {learning.confidence_update >= 0 ? "+" : ""}
                {(learning.confidence_update * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Key Takeaways */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            <h3 className="section-header">Key Takeaways</h3>
          </div>
          <button 
            onClick={() => navigate("/promotioniq/summary")}
            className="btn-athena text-sm"
          >
            View Executive Summary
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
        <ul className="space-y-3">
          <li 
            onClick={() => navigate("/promotioniq/retailer-economics/R1")}
            className="flex items-start gap-2 text-sm cursor-pointer hover:bg-primary/10 p-2 -m-2 transition-colors"
          >
            <span className="text-primary">•</span>
            <span>Mediterranean line outperforms other product lines consistently - prioritize in promotions</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
          </li>
          <li 
            onClick={() => navigate("/promotioniq/impact-panel")}
            className="flex items-start gap-2 text-sm cursor-pointer hover:bg-primary/10 p-2 -m-2 transition-colors"
          >
            <span className="text-primary">•</span>
            <span>TikTok + Instagram combo delivers 35% incremental lift on social-driven promotions</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
          </li>
          <li 
            onClick={() => navigate("/promotioniq/retailer-economics/R2")}
            className="flex items-start gap-2 text-sm cursor-pointer hover:bg-primary/10 p-2 -m-2 transition-colors"
          >
            <span className="text-primary">•</span>
            <span>Whole Foods Market weekly cadence amplifies pantry loading - cap discounts at 18%</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
          </li>
          <li 
            onClick={() => navigate("/promotioniq/negotiation")}
            className="flex items-start gap-2 text-sm cursor-pointer hover:bg-primary/10 p-2 -m-2 transition-colors"
          >
            <span className="text-primary">•</span>
            <span>Wegman's Market premium positioning supports lower discount depths with strong ROI</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
          </li>
        </ul>
      </div>

      {/* Related Screens */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div 
          onClick={() => navigate("/promotioniq/baseline-diagnostics")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Update Baselines</p>
                <p className="text-sm text-muted-foreground">Recalibrate forecasts</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/scenarios")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="h-5 w-5 text-warning" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Run Scenarios</p>
                <p className="text-sm text-muted-foreground">Apply learnings to future</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/negotiation")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-success" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Negotiation Prep</p>
                <p className="text-sm text-muted-foreground">Use evidence in meetings</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Learnings;
